import { useCallback, useEffect, useState } from "react";
import api from "../../api/axios";
import { Bar, Banner, ROLES, Section, Stat, digits, errText, fmt } from "./cabinet-ui";
import { EarningsTab, OverviewTab, SalaryTab } from "./SheetTabs";

type Sub = "overview" | "salary" | "earnings";

function SettingsForm({ sheet, onSaved }: { sheet: any; onSaved: () => void }) {
  const [base, setBase] = useState(String(sheet.settings.baseSalary || ""));
  const [planE, setPlanE] = useState(String(sheet.settings.planEarnings || ""));
  const [planD, setPlanD] = useState(String(sheet.settings.planDeals || ""));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put(`/payroll/settings/${sheet.user.id}`, { baseSalary: Number(base || 0), planEarnings: Number(planE || 0), planDeals: Number(planD || 0) });
      setMsg({ ok: true, text: "Сохранено" });
      onSaved();
    } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setSaving(false); }
  };
  return (
    <Section title="Оклад и план (действуют на все месяцы)">
      <div className="cb-form">
        {msg && <Banner kind={msg.ok ? "ok" : "err"} onClose={() => setMsg(null)}>{msg.text}</Banner>}
        <div className="cb-form-row">
          <label>Оклад, ₸<input type="text" inputMode="numeric" value={base} onChange={(e) => setBase(digits(e.target.value))} /></label>
          <label>План по заработку, ₸<input type="text" inputMode="numeric" value={planE} onChange={(e) => setPlanE(digits(e.target.value))} /></label>
          <label>План по сделкам, шт<input type="text" inputMode="numeric" value={planD} onChange={(e) => setPlanD(digits(e.target.value))} /></label>
          <button type="button" className="cb-btn" onClick={save} disabled={saving}>{saving ? "…" : "Сохранить"}</button>
        </div>
      </div>
    </Section>
  );
}

export default function TeamTab({ month, canManage, meId }: { month: string; canManage: boolean; meId: number }) {
  const [data, setData] = useState<any>(null);
  const [loadErr, setLoadErr] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const [sheet, setSheet] = useState<any>(null);
  const [sub, setSub] = useState<Sub>("overview");
  const [q, setQ] = useState("");

  const loadTeam = useCallback(async () => {
    setLoadErr("");
    try { setData((await api.get(`/payroll/team?month=${month}`)).data); }
    catch (e) { setLoadErr(errText(e, "Не удалось загрузить сотрудников")); }
  }, [month]);
  const loadSheet = useCallback(async () => {
    if (selected == null) { setSheet(null); return; }
    try { setSheet((await api.get(`/payroll/user/${selected}?month=${month}`)).data); setLoadErr(""); }
    catch (e) { setLoadErr(errText(e, "Не удалось загрузить ведомость")); setSelected(null); }
  }, [selected, month]);

  useEffect(() => { loadTeam(); }, [loadTeam]);
  useEffect(() => { loadSheet(); }, [loadSheet]);
  const refresh = () => { loadSheet(); loadTeam(); };

  if (selected != null) {
    return (
      <>
        <button type="button" className="cb-link" onClick={() => { setSelected(null); setSub("overview"); }}>← Все сотрудники</button>
        {!sheet ? <div className="cb-empty">Загрузка…</div> : (
          <>
            <h3 className="cb-person">{sheet.user.name} <span className="cb-sub">{ROLES[sheet.user.role] ?? ""}</span></h3>
            <div className="cb-subtabs" role="tablist">
              {([["overview", "Обзор"], ["salary", "Зарплата"], ["earnings", "Заработок"]] as [Sub, string][]).map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={sub === id} className={sub === id ? "active" : ""} onClick={() => setSub(id)}>{label}</button>
              ))}
            </div>
            {sub === "overview" && (
              <>
                <OverviewTab sheet={sheet} />
                {canManage && <SettingsForm key={sheet.user.id + ":" + sheet.month} sheet={sheet} onSaved={refresh} />}
              </>
            )}
            {sub === "salary" && <SalaryTab sheet={sheet} onChanged={refresh} />}
            {sub === "earnings" && <EarningsTab sheet={sheet} />}
          </>
        )}
      </>
    );
  }

  if (loadErr) return <Banner kind="err" onClose={loadTeam}>{loadErr}. Нажмите ✕, чтобы повторить.</Banner>;
  if (!data) return <div className="cb-empty">Загрузка…</div>;
  const all: any[] = data.people ?? [];
  const people = all.filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()));
  if (!all.length) return <div className="cb-empty">Нет сотрудников</div>;
  const total = people.reduce((s, p) => ({ earned: s.earned + p.earned, accrued: s.accrued + p.accrued, paid: s.paid + p.paid, toPay: s.toPay + p.toPay }), { earned: 0, accrued: 0, paid: 0, toPay: 0 });
  return (
    <>
      <div className="cb-stats">
        <Stat label="Заработано с продаж" value={fmt(total.earned)} tone="accent" />
        <Stat label="Начислено" value={fmt(total.accrued)} />
        <Stat label="Выплачено" value={fmt(total.paid)} />
        <Stat label="К выплате" value={fmt(total.toPay)} tone={total.toPay > 0 ? "warn" : undefined} />
      </div>
      <input className="cb-search" type="search" placeholder="Найти сотрудника…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="cb-people">
        {people.map((p) => (
          <button key={p.id} type="button" className="cb-person-card" onClick={() => setSelected(p.id)} aria-label={`Открыть ведомость: ${p.name}`}>
            <div className="cb-person-top">
              <div>
                <b>{p.name}</b>{p.id === meId && <span className="cb-me"> вы</span>}
                <div className="cb-sub">{ROLES[p.role] ?? p.role} · сделок {p.dealsCount}</div>
              </div>
              <div className="cb-person-pay">
                <span>к выплате</span>
                <b>{fmt(p.toPay)}</b>
              </div>
            </div>
            <div className="cb-person-grid">
              <div><span>Заработок</span>{fmt(p.earned)}</div>
              <div><span>Начислено</span>{fmt(p.accrued)}</div>
              <div><span>Выплачено</span>{fmt(p.paid)}</div>
            </div>
            {p.planPct != null && (
              <div className="cb-mini"><Bar pct={p.planPct} /><span>план {p.planPct}%</span></div>
            )}
          </button>
        ))}
        {!people.length && <div className="cb-empty">Никого не найдено</div>}
      </div>
      <div className="cb-hint">Откройте сотрудника, чтобы увидеть ведомость{canManage ? ", задать оклад и план, внести премию, штраф или выплату" : ""}.</div>
    </>
  );
}
